import React, { useEffect, useState } from 'react';
import { useMentorAuth } from '../contexts/MentorAuthContext';
import { useChat } from '../contexts/ChatContext';
import { toast } from 'react-toastify';
import ConversationList from '../components/chat/ConversationList';
import MessageList from '../components/chat/MessageList';
import MessageInput from '../components/chat/MessageInput';

const MentorChatPage = () => {
  const { mentor } = useMentorAuth();
  const {
    conversations,
    activeConversation,
    setActiveConversation,
    messages,
    isConnected,
    typingUsers,
    sendMessage,
    sendTyping,
    markAsRead,
    uploadFile,
    fetchMessages,
    fetchConversations
  } = useChat();

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (activeConversation) {
      fetchMessages(activeConversation.id);
      markAsRead(activeConversation.id);
    }
  }, [activeConversation, fetchMessages, markAsRead]);

  const handleSelectConversation = (conversation) => {
    setActiveConversation(conversation);
  };

  const handleSendMessage = (content) => {
    if (activeConversation) {
      sendMessage(activeConversation.id, content, 'text', null);
    }
  };

  const handleTyping = () => {
    if (activeConversation) {
      sendTyping(activeConversation.id);
    }
  };

  const handleFileUpload = async (file) => {
    if (!activeConversation) return;

    try {
      const result = await uploadFile(file);
      if (result?.file_url) {
        sendMessage(activeConversation.id, `Archivo: ${file.name}`, 'file', result.file_url);
        // No mostrar notificación - el usuario ya sabe que envió el archivo
      }
    } catch (error) {
      toast.error('Error al enviar el archivo');
    }
  };

  const currentMessages = activeConversation ? messages[activeConversation.id] || [] : [];
  const typingUser = activeConversation ? typingUsers[activeConversation.id] : null;

  // Filter conversations to show only students (mentors should only chat with their students)
  const studentConversations = conversations.filter(
    conv => conv.participant_type === 'student' || !conv.participant_type
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Chat con Estudiantes</h1>
                <p className="text-sm text-gray-500">
                  {isConnected ? (
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Conectado
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                      Desconectado
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            <div className="text-sm text-gray-600">
              <span className="font-medium">{studentConversations.length}</span> estudiante{studentConversations.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{ height: 'calc(100vh - 180px)' }}>
          <div className="flex h-full">
            {/* Conversations sidebar */}
            <div className="w-80 border-r bg-white flex flex-col">
              {/* Sidebar header */}
              <div className="p-4 border-b bg-gradient-to-r from-purple-50 to-pink-50">
                <h2 className="text-lg font-semibold text-gray-900">Mis Estudiantes</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Conversaciones activas
                </p>
              </div>

              {/* Conversation list */}
              {studentConversations.length > 0 ? (
                <ConversationList
                  conversations={studentConversations}
                  activeConversationId={activeConversation?.id}
                  onSelectConversation={handleSelectConversation}
                  currentUserId={mentor?.id}
                />
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-400 p-8">
                  <div className="text-center">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="text-sm font-medium">No hay conversaciones</p>
                    <p className="text-xs mt-1">Los estudiantes pueden iniciar conversaciones contigo</p>
                  </div>
                </div>
              )}
            </div>

            {/* Chat area */}
            <div className="flex-1 flex flex-col">
              {activeConversation ? (
                <>
                  {/* Chat header */}
                  <div className="px-6 py-4 border-b bg-gradient-to-r from-purple-50 to-pink-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                          style={{ backgroundColor: activeConversation.participant_avatar_color || '#6366f1' }}
                        >
                          {activeConversation.participant_avatar_url ? (
                            <img
                              src={activeConversation.participant_avatar_url}
                              alt={activeConversation.participant_name}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <span>{activeConversation.participant_name?.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {activeConversation.participant_name}
                          </h3>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                            👤 Estudiante
                          </span>
                        </div>
                      </div>

                      {/* Quick actions */}
                      <div className="flex items-center space-x-2">
                        <button
                          className="p-2 text-gray-600 hover:bg-white rounded-lg transition-colors"
                          title="Ver perfil del estudiante"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <MessageList
                    messages={currentMessages}
                    currentUserId={mentor?.id}
                    typingUser={typingUser}
                  />

                  {/* Message input */}
                  <MessageInput
                    onSend={handleSendMessage}
                    onTyping={handleTyping}
                    onFileUpload={handleFileUpload}
                    disabled={!isConnected}
                  />
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <svg className="w-24 h-24 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Selecciona un estudiante</h3>
                    <p className="text-sm">Elige una conversación de la izquierda para comenzar a chatear</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentorChatPage;
